import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button, OverlayTrigger, Tooltip, Alert, Modal } from 'react-bootstrap';
import { BiEdit, BiTrash, BiSearch, BiSortUp, BiSortDown } from 'react-icons/bi';
import './RulesLayout.scss';

const ClosingRules = () => {
  const [rules, setRules] = useState([]);
  const [search, setSearch] = useState({
    origins: '',
    destinations: ''
  });
  const [sortedRules, setSortedRules] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState(location.state?.successMessage || '');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 10000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (!showDeleteModal) {
      fetchRules();
    }
  }, [search]);

  useEffect(() => {
    let sortedArray = [...rules];
    if (sortConfig.key !== null) {
      sortedArray.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    setSortedRules(sortedArray);
  }, [rules, sortConfig]);

  const fetchRules = async () => {
    try {
      const response = await axios.get('/api/closing-rules', { params: search });
      setRules(response.data);
    } catch (error) {
      console.error('Error fetching closing rules', error);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearch((prevSearch) => ({
      ...prevSearch,
      [name]: value
    }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchRules();
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction === 'asc' ? <BiSortUp /> : <BiSortDown />;
  };

  const handleDelete = (rule) => {
    setSelectedRule(rule);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/api/closing-rule/${selectedRule.closing_rule_id}`);
      setSuccessMessage('Closing rule deleted successfully.');
      fetchRules();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting closing rule', error);
    }
  };

  return (
    <div>
      <h2>Closing Rules</h2>
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
          {successMessage}
        </Alert>
      )}
      <Link to="/rules/closing/add" className="btn btn-primary">Create Closing Rule</Link>
      <form className="search-fields" onSubmit={handleSearchSubmit}>
        <input
          type="text"
          name="origins"
          placeholder="Origin(s)"
          value={search.origins}
          onChange={handleSearchChange}
        />
        <input
          type="text"
          name="destinations"
          placeholder="Destination(s)"
          value={search.destinations}
          onChange={handleSearchChange}
        />
        <Button type="submit" variant="primary" className="search-button">
          <BiSearch />
        </Button>
      </form>
      {rules.length === 0 ? (
        <p>No closing rules found</p>
      ) : (
        <table className="table table-striped">
          <thead>
            <tr>
              <th onClick={() => requestSort('origin_port_list')}>Origins {getSortIcon('origin_port_list')}</th>
              <th onClick={() => requestSort('destination_port_list')}>Destinations {getSortIcon('destination_port_list')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedRules.map((rule) => (
              <tr key={rule.closing_rule_id}>
                <td>{rule.origin_port_list}</td>
                <td>{rule.destination_port_list}</td>
                <td>
                  <OverlayTrigger
                    overlay={<Tooltip id={`tooltip-edit-${rule.closing_rule_id}`}>Edit the closing rule</Tooltip>}
                  >
                    <Button variant="warning" className="action-button" as={Link} to={`/rules/closing/edit/${rule.closing_rule_id}`}>
                      <BiEdit />
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    overlay={<Tooltip id={`tooltip-delete-${rule.closing_rule_id}`}>Delete the closing rule</Tooltip>}
                  >
                    <Button variant="danger" className="action-button" onClick={() => handleDelete(rule)}>
                      <BiTrash />
                    </Button>
                  </OverlayTrigger>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Closing Rule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Deleting this record is permanent and cannot be undone.</p>
          {selectedRule && (
            <table className="table">
              <thead>
                <tr>
                  <th>Origin(s)</th>
                  <th>Destination(s)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{selectedRule.origin_port_list}</td>
                  <td>{selectedRule.destination_port_list}</td>
                </tr>
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete}>Delete Closing Rule</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ClosingRules;

